import { useContext, useState } from 'react';
import Image from 'next/future/image';
import { NextRouter, useRouter } from 'next/router';
import cn from 'classnames';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { CartContext } from 'pages/cart';
import { RC_DURATION } from '@lambda/reebelo';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import useCart, {
  AddCartFunc,
  encodeDecodeForAttr,
  generateIDAttr,
} from '@/lib/use-cart';
import { ItemTypeWithProps } from '../cart/types';
import useAttributes from '@/lib/use-attributes';
import rCareIcon from '@/public/icons/cart/rCareNew.svg';
import { priceFormater } from '@/settings/index';
import WarrantySlideOver from '../collections/[handle]/WarrantySlideOver';
import InfoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import InfoIconWhite from '@/public/icons/product/infoIconWhite.svg';
import {
  generateReebeloCareSpecs,
  generateReebeloCareTitle,
} from '@/lib/reebelocare/helpers';
import { ShipmentLineItem } from '@/lib/smartForm/types';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';
import { ReebeloCareTrackRequest } from '@/lib/analytics/types';

type AddReebeloCareToCartArgs = {
  add: AddCartFunc;
  loading: boolean;
  quantity: number;
  variantId: number;
  attributes?: Array<{ key: string; value: string }>;
  router: NextRouter;
  warrantyAddedToCart: boolean;
  hideModal: () => void;
  itemCondition: string;
  itemPsku: string;
  cartSize: number;
  orderId: string;
  lineItem: ShipmentLineItem;
};

const GID_VAR = 'gid://shopify/ProductVariant/';
const getMerchandiseId = (variantId: number) => `${GID_VAR}${variantId}`;

const handleAddToCart =
  ({
    add,
    loading,
    quantity,
    variantId,
    attributes,
    router,
    warrantyAddedToCart,
    hideModal,
    lineItem,
    orderId,
  }: AddReebeloCareToCartArgs): (() => void) =>
  () => {
    if (loading || !variantId || warrantyAddedToCart) return;

    const merchandiseId = getMerchandiseId(variantId);

    add({
      quantity,
      attributes,
      merchandiseId,
    }).then(() => {
      const { psku } = extractTags(lineItem.tags);
      const trackingProps: ReebeloCareTrackRequest = {
        entry_point: 'Account',
        reebelocare_id: variantId,
        device_psku: psku ?? '',
        device_vendor: lineItem.vendor,
        device_sku: lineItem.sku,
        duration: RC_DURATION['24_MONTH'],
        previous_order_number: orderId,
      };

      trackReebeloCareAdded(trackingProps);
      router.push('/cart');
      hideModal();
    });
  };

const AddReebeloCareCta = ({
  productWarranty,
  orderNumber,
  warrantyAddedToCart,
  lineItem,
}: {
  productWarranty: WarrantyT;
  orderNumber: string;
  warrantyAddedToCart: boolean;
  lineItem: ShipmentLineItem;
}) => {
  const { cartCount } = useContext(CartContext);
  const router = useRouter();
  const [showModal, setShowModal] = useState(false);

  const rCareTitle = generateReebeloCareTitle();
  const rCareSpecs = [generateReebeloCareSpecs()];

  const { sku, name, vendor, tags, quantity } = lineItem;

  const { price, shopifyVariantId } = productWarranty;

  const attributes = [
    ...encodeDecodeForAttr.encode({
      sku,
      title: name,
    }),
    ...(generateIDAttr(vendor, sku) as any),
  ];

  const product = {
    vendor: 'Reebelo',
    title: rCareTitle,
    specs: rCareSpecs,
    imageSrc: rCareIcon,
    price,
    variantId: shopifyVariantId,
  };

  const { loading, add } = useCart();

  const { addCartDateTimeAttribute } = useAttributes({
    product,
    type: 'warranty',
  } as ItemTypeWithProps);

  const hideModal = () => setShowModal(false);

  const extractedTags = extractTags(tags);

  const onUpgradeWarrantyClick = () => {
    setShowModal(true);
  };

  const totalPrice = price * quantity;
  const totalMonthlyPrice = price / Number(RC_DURATION['24_MONTH']);

  return (
    <div className="mt-2" id="post-purchase-reebelocare">
      <button
        className={cn(
          'enabled:hover:bg-gray-700 enabled:hover:text-white group flex cursor-pointer items-center gap-2 rounded-md border border-gray-700 p-2 text-xs disabled:cursor-not-allowed disabled:opacity-50',
        )}
        onClick={() => onUpgradeWarrantyClick()}
        disabled={warrantyAddedToCart}
      >
        Upgrade Warranty for {priceFormater.short(totalPrice)} (
        {priceFormater.short(totalMonthlyPrice)}/mo)
        <div className="h-3 w-3">
          <Image
            src={InfoIconBlack}
            alt="Info"
            height={20}
            width={20}
            className="group-hover:group-enabled:hidden h-full w-full"
          />
          <Image
            src={InfoIconWhite}
            alt="Info"
            height={20}
            width={20}
            className="group-hover:group-enabled:block hidden h-full w-full"
          />
        </div>
      </button>
      <WarrantySlideOver
        isVisible={showModal}
        hideModal={hideModal}
        warrantyPurchased={warrantyAddedToCart}
        purchaseWarranty={handleAddToCart({
          add,
          loading,
          warrantyAddedToCart,
          quantity,
          variantId: shopifyVariantId,
          attributes: [
            ...attributes,
            { key: 'PREVIOUS_ORDER_ID', value: `${orderNumber}` },
            ...addCartDateTimeAttribute,
            { key: 'Duration', value: RC_DURATION['24_MONTH'] },
          ],
          router,
          hideModal,
          itemCondition: extractedTags?.condition ?? '',
          itemPsku: extractedTags?.psku ?? '',
          cartSize: cartCount,
          orderId: `${orderNumber}`,
          lineItem,
        })}
        price={price}
        duration={Number(RC_DURATION['24_MONTH'])} // We only support 24 months under account
      />
    </div>
  );
};

export default AddReebeloCareCta;
